import { Box, Circle, Flex, VStack } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FavoriteIcon,
  FavoriteSolidIcon,
  FileIcon,
  FileSolidIcon,
  HelpIcon,
  HelpSolidIcon,
  HomeIcon,
  HomeSolidIcon,
  SearchIcon,
  SearchSolidIcon,
  SidebarSolidIcon,
  SidebarIcon,
  UserIcon,
  UserSolidIcon,
  ExitIcon,
  SettingsIcon,
  SettingsSolidIcon,
  DocumentIcon,
} from 'assets/icons/v2';
import React, { ReactElement } from 'react';
import { ExternalRoutes, ProtectedRoutes } from 'types/Routes';
import { ExtendedLogo } from 'assets/common/ExtendedLogo';
import { useLogout, useUser } from 'utils/auth';
import { useIsDesktopNavOpen } from 'hooks/utils/layout/useIsDesktopNavOpen';
import { Z_INDEX } from 'styles/zIndex';
import { isSubscriptionStatus } from 'utils/isSubscriptionStatus';
import { Role, SchoolUserRole, SubscriptionStatus } from '@novaheal/types';
import { useIsTeacherOrAdmin } from 'hooks/utils/useIsTeacherOrAdmin';
import { NavigationLink } from './NavigationLink';
import { NavigationLinkWithDropdown } from './NavigationLinkWithDropdown';

export const closedWidth = 72;

type PanelLinks = {
  to: ProtectedRoutes | ExternalRoutes;
  title: string;
  isExternal?: boolean;
};

export const NavigationBarDesktop = (): ReactElement => {
  const { data: user } = useUser();
  const isPaidUser = isSubscriptionStatus(user, SubscriptionStatus.PAID);
  const location = useLocation();
  const { isOpen, toggle } = useIsDesktopNavOpen();
  const logout = useLogout();
  const navigate = useNavigate();

  const width = isOpen ? '215px' : `${closedWidth}px`;
  const logoTransform = isOpen ? 'scale(2)' : undefined;
  const transition = '0.4s ease-in-out';

  const isProfilePage =
    (location.pathname.startsWith(ProtectedRoutes.SETTINGS) &&
      location.pathname !== ProtectedRoutes.SUPPORT) ||
    location.pathname === ProtectedRoutes.PAYMENT_MANAGEMENT ||
    location.pathname === ProtectedRoutes.AI_SUBSCRIPTION_MANAGEMENT ||
    location.pathname === ProtectedRoutes.AI_SUBSCRIPTION_REDIRECT;

  const profilePanelLinks: PanelLinks[] = [
    { to: ProtectedRoutes.PROFILE, title: 'Persönliche Daten' },
    { to: ProtectedRoutes.PASSWORD, title: 'Passwort ändern' },
    { to: ProtectedRoutes.VOUCHER, title: 'Zugangscode eingeben' },
    { to: ProtectedRoutes.AI_SUBSCRIPTION_MANAGEMENT, title: 'KI Pakete' },
    { to: ProtectedRoutes.REFERRAL, title: 'Freundschaftscode' },
    { to: ProtectedRoutes.PAYMENT_MANAGEMENT, title: 'Premium Management' },
    { to: ProtectedRoutes.LEGAL, title: 'Rechtliches' },
  ];
  const supportPanelLinks: PanelLinks[] = [
    {
      to: ExternalRoutes.NOVAHEAL,
      title: 'Novaheal Infos',
      isExternal: true,
    },
    {
      to: ExternalRoutes.HELP,
      title: 'Hilfe & Kontakt',
      isExternal: true,
    },
  ];

  const adminPanelLinks: PanelLinks[] = [
    { to: ProtectedRoutes.ORG_MANAGEMENT_VOUCHER_DASHBOARD, title: 'Lizenzen' },
    { to: ProtectedRoutes.ORG_MANAGEMENT_USER_DASHBOARD, title: 'Nutzer:innen' },
    { to: ProtectedRoutes.ORG_MANAGEMENT_CLASS_DASHBOARD, title: 'Kurse' },
  ];

  const isTeacherOrAdmin = useIsTeacherOrAdmin();

  const { schoolRole: userRole } = user ?? {};

  const isUserSchoolAdmin = userRole?.roles?.includes(SchoolUserRole.ADMIN) ?? false;
  const isTeacher = user?.educationStatus === 'TEACH';
  const isGuide = user?.educationStatus === 'GUIDE';

  // returns the filtered PanelLinks based on the premium status of the user (Premium User can see Premium management)
  const getModifiedProfilePanelLinks = (): PanelLinks[] => {
    let filteredLinks = [...profilePanelLinks];

    // Filter out premium management for non-paid users
    if (!isPaidUser) {
      filteredLinks = filteredLinks.filter(
        (link) => link.to !== ProtectedRoutes.PAYMENT_MANAGEMENT
      );
    }

    if (user?.subscription?.status === SubscriptionStatus.VOUCHER || !(isTeacher || isGuide)) {
      filteredLinks = filteredLinks.filter(
        (link) => link.to !== ProtectedRoutes.AI_SUBSCRIPTION_MANAGEMENT
      );
    }

    return filteredLinks;
  };

  return (
    <Box
      h="100vh"
      maxH="100vh"
      bg="background.tertiary"
      position="sticky"
      top="0"
      flexShrink="0"
      w={width}
      pt="l"
      color="menu.text.secondary"
      transition={transition}
      zIndex={Z_INDEX.NAVBAR}
      // used for the modal centering
      data-id="navigation-bar-desktop"
    >
      <Circle
        size="40px"
        padding="5px"
        position="absolute"
        top="14px"
        right="0"
        transform="translateX(40%)"
        cursor="pointer"
        onClick={toggle}
        backgroundColor="background.tertiary"
        zIndex={1}
      >
        <Circle size="100%" bg="#ffffff4c;">
          {isOpen ? (
            <SidebarSolidIcon boxSize="20px" color="icon.onAccent" />
          ) : (
            <SidebarIcon boxSize="20px" color="icon.onAccent" />
          )}
        </Circle>
      </Circle>
      <Flex flexDirection="column" height="100%" justifyContent="space-between" paddingTop="35px">
        <Flex flexShrink={0} justifyContent="center">
          <Box h="70px" onClick={() => navigate(ProtectedRoutes.HOME)}>
            <ExtendedLogo
              w="50px"
              h="40px"
              transform={logoTransform}
              transition={transition}
              opacity="1"
              bgVariant="dark"
            />
          </Box>
        </Flex>

        <Flex
          direction="column"
          alignItems="flex-start"
          flex={1}
          overflowY="scroll"
          paddingTop="60px"
          scrollbar="hidden"
        >
          <VStack align="left" width="100%" gap="10">
            <NavigationLink
              to={ProtectedRoutes.HOME}
              iconInactive={<HomeIcon />}
              iconActive={<HomeSolidIcon />}
              isActive={
                location.pathname === ProtectedRoutes.HOME ||
                location.pathname.startsWith(ProtectedRoutes.NEWS) ||
                location.pathname.startsWith(ProtectedRoutes.CURATED_LIST)
              }
              title="Home"
              hasTitle={isOpen}
            />
            <NavigationLink
              to={ProtectedRoutes.KNOWLEDGE}
              iconInactive={<SearchIcon />}
              iconActive={<SearchSolidIcon />}
              isActive={
                location.pathname.startsWith(ProtectedRoutes.KNOWLEDGE) ||
                location.pathname.startsWith(ProtectedRoutes.SEARCH)
              }
              title="Kategorien"
              hasTitle={isOpen}
            />
            <NavigationLink
              to={ProtectedRoutes.FAVORITES}
              iconInactive={<FavoriteIcon />}
              iconActive={<FavoriteSolidIcon />}
              isActive={location.pathname.startsWith(ProtectedRoutes.FAVORITES)}
              title="Favoriten"
              hasTitle={isOpen}
            />

            {isTeacherOrAdmin && (
              <NavigationLink
                to={ProtectedRoutes.WORKSHEET_DASHBOARD}
                iconInactive={<FileIcon />}
                iconActive={<FileSolidIcon />}
                isActive={location.pathname.startsWith(ProtectedRoutes.WORKSHEET_DASHBOARD)}
                title="Arbeitsblätter"
                hasTitle={isOpen}
              />
            )}

            {isTeacherOrAdmin && user?.role === Role.BETA && (
              <NavigationLink
                to={ProtectedRoutes.SESSION_PLAN}
                iconInactive={<DocumentIcon />}
                iconActive={<DocumentIcon />}
                isActive={location.pathname.startsWith(ProtectedRoutes.SESSION_PLAN)}
                title="Unterrichtsplaner"
                hasTitle={isOpen}
              />
            )}

            {isUserSchoolAdmin && (
              <NavigationLinkWithDropdown
                to={ProtectedRoutes.ORG_MANAGEMENT_USER_DASHBOARD}
                iconInactive={<SettingsIcon />}
                iconActive={<SettingsSolidIcon />}
                isActive={
                  location.pathname.startsWith(ProtectedRoutes.ORG_MANAGEMENT_USER_DASHBOARD) ||
                  location.pathname.startsWith(ProtectedRoutes.ORG_MANAGEMENT_VOUCHER_DASHBOARD) ||
                  location.pathname.startsWith(ProtectedRoutes.ORG_MANAGEMENT_CLASS_DASHBOARD)
                }
                title="Verwaltung"
                hasTitle={isOpen}
                panelLinks={adminPanelLinks}
              />
            )}

            <NavigationLinkWithDropdown
              to={ProtectedRoutes.SETTINGS}
              iconInactive={<UserIcon />}
              iconActive={<UserSolidIcon />}
              hasTitle={isOpen}
              isActive={isProfilePage}
              title="Profil"
              panelLinks={getModifiedProfilePanelLinks()}
            />
            <Box
              h="2px"
              w={`calc(100% - ${closedWidth / 2}px)`}
              bg="divider.default"
              pos="relative"
              left="50%"
              transform="translateX(-50%)"
            />
          </VStack>

          <VStack align="left" gap="10" mt="30px" paddingBottom="40px">
            <NavigationLinkWithDropdown
              to={ProtectedRoutes.SUPPORT}
              iconInactive={<HelpIcon />}
              iconActive={<HelpSolidIcon />}
              isActive={location.pathname === ProtectedRoutes.SUPPORT}
              title="Support"
              hasTitle={isOpen}
              panelLinks={supportPanelLinks}
            />
            <NavigationLink
              to={ProtectedRoutes.SUPPORT}
              iconInactive={<ExitIcon />}
              isActive={false}
              title="Logout"
              isOnClickLink
              isInternalLink={false}
              onClick={() => logout.mutate({})}
              hasTitle={isOpen}
            />
          </VStack>
        </Flex>
      </Flex>
    </Box>
  );
};
